import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { apiRequest, apiFormRequest } from '../../utils';
import './MemberHome.css';

/* An approved specialist can put one of their own articles forward for the Bravos content feed.
   It is a PROPOSAL: it reaches nobody until we publish it, and the rules are stated above the
   form rather than discovered by having a submission declined.

   Only rendered for a specialist whose listing is approved — the endpoint answers can_submit:
   false for everyone else, so the block simply doesn't appear. */
const BlogSubmission = () => {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [summary, setSummary] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const anchor = useRef(null);
  // The file input is uncontrolled, so clearing `file` on success is not enough — the picked
  // name would stay on screen next to an empty form. Reset the element itself.
  const fileInput = useRef(null);

  const load = () => apiRequest('GET', '/bravos/blog-submission')
    .then(setState)
    .catch(() => setState({ can_submit: false, posts: [] }));

  useEffect(() => { load(); }, []);

  /* The member home links straight here with #submit-article. The form is the last thing on
     the screen, so land on it rather than at the top of the profile the member didn't come
     to edit — and only once it has actually rendered. */
  useEffect(() => {
    if (state && state.can_submit && window.location.hash === '#submit-article') {
      anchor.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [state]);

  const submit = (e) => {
    e.preventDefault();
    if (busy || !title.trim() || !url.trim()) return;
    setBusy(true);
    setNotice(null);
    // apiRequest always sends JSON, so a picture needs the form variant. Same fields either
    // way — the picture is the only reason the request changes shape.
    const fields = { title: title.trim(), url: url.trim(), summary: summary.trim() };
    let send;
    if (file) {
      const form = new FormData();
      Object.entries(fields).forEach(([k, v]) => { if (v) form.append(k, v); });
      form.append('file', file);
      send = apiFormRequest('/bravos/blog-submission', form);
    } else {
      send = apiRequest('POST', '/bravos/blog-submission', fields);
    }
    send
      .then(() => {
        setNotice(t('blogSubmitted'));
        setTitle(''); setUrl(''); setSummary(''); setFile(null);
        if (fileInput.current) fileInput.current.value = '';
        load();
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: err.message })))
      .finally(() => setBusy(false));
  };

  if (!state || !state.can_submit) return null;
  return (
    <>
      <div id="submit-article" ref={anchor} />
      <Card>
        <Title my="1" className="mt-2">
          <Icon name="newspaper" theme="primary" className="me-2" />
          {t('submitArticle')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('submitArticleRules')}</p>

        <form onSubmit={submit}>
          <label className="bravos-form__label">{t('articleTitle')}</label>
          <input className="bravos-form__input" type="text" value={title} maxLength={200}
                 onChange={(e) => setTitle(e.target.value)} required />

          <label className="bravos-form__label">{t('articleLink')}</label>
          <input className="bravos-form__input" type="url" value={url}
                 placeholder="https://…" onChange={(e) => setUrl(e.target.value)} required />

          <label className="bravos-form__label">{t('articleSummary')}</label>
          <textarea className="bravos-form__input" rows={3} value={summary} maxLength={400}
                    onChange={(e) => setSummary(e.target.value)} />

          <label className="bravos-form__label">
            {t('articleImage')}
            <input ref={fileInput} type="file" accept="image/*"
                   onChange={(e) => setFile(e.target.files[0] || null)} />
          </label>
          <p className="bravos-q__hint">{t('articleImageHelp')}</p>

          {notice && <p className="bravos-cal__notice mt-2">{notice}</p>}
          <button type="submit" className="bravos-event__claim mt-2"
                  disabled={busy || !title.trim() || !url.trim()}>
            {busy ? '…' : t('submitArticle')}
          </button>
        </form>

        {(state.posts || []).length > 0 && (
          <>
            <p className="bravos-cal__label mt-3">{t('mySubmissions')}</p>
            {state.posts.map((p) => (
              <div key={p.id} className="bravos-member__alloc">
                <span className="bravos-member__alloc-name">{p.title}</span>
                <span className={`bravos-cta__tag ${p.status === 'approved' ? 'bravos-cta__tag--done' : ''}`}>
                  {t(`blogStatus.${p.status}`)}
                </span>
              </div>
            ))}
          </>
        )}
      </Card>
    </>
  );
};

/* Edit the member's OWN specialist profile — headline, services (one per line), a member-discount
   note, and a "visible in the directory" toggle. Backend: GET/POST /api/bravos/specialist-profile. */
const EditSpecialistProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [approved, setApproved] = useState(false);
  const [headline, setHeadline] = useState('');
  const [services, setServices] = useState('');
  const [discountNote, setDiscountNote] = useState('');
  const [commission, setCommission] = useState('');
  const [publicBio, setPublicBio] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [twitter, setTwitter] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    apiRequest('GET', '/bravos/specialist-profile')
      .then((p) => {
        setVisible(!!p.visible);
        setApproved(!!p.approved);
        setHeadline(p.headline || '');
        setServices((p.services || []).join('\n'));
        setDiscountNote(p.discount_note || '');
        setCommission(p.commission || '');
        setPublicBio(p.public_bio || '');
        setLinkedin(p.linkedin || '');
        setInstagram(p.instagram || '');
        setFacebook(p.facebook || '');
        setTwitter(p.twitter || '');
        setWebsite(p.website || '');
      })
      .catch(() => navigate('/member-home'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const save = (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    apiRequest('POST', '/bravos/specialist-profile', {
      visible,
      headline: headline.trim(),
      services: services.split('\n').map((s) => s.trim()).filter(Boolean),
      discount_note: discountNote.trim(),
      commission: commission.trim(),
      public_bio: publicBio.trim(),
      linkedin: linkedin.trim(),
      instagram: instagram.trim(),
      facebook: facebook.trim(),
      twitter: twitter.trim(),
      website: website.trim(),
    })
      .then((res) => { setNotice(t('profileSaved')); setApproved(!!(res && res.approved)); })
      .catch(() => setNotice(t('saveFailed')))
      .finally(() => setSaving(false));
  };

  return (
    <Base activeNav="home">
      <Card>
        <div className="bravos-brand">
          <div><Title my="0" className="mt-1">{t('editMyProfile')}</Title></div>
          <button type="button" className="bravos-wallet__link bravos-members__back"
                  onClick={() => navigate('/specialists')}>
            <Icon name="angle-left" /> {t('back')}
          </button>
        </div>
      </Card>

      {loading ? (
        <Card><span className="auth__spinner" /></Card>
      ) : (
        <Card>
          <form onSubmit={save}>
            <label className="bravos-form__toggle">
              <input type="checkbox" checked={visible}
                     onChange={(e) => setVisible(e.target.checked)} />
              <span>{t('requestListing')}</span>
            </label>
            <p className="bravos-q__hint">{t('requestListingHelp')}</p>
            {visible && (
              <p className={`bravos-spec__status ${approved ? 'is-approved' : 'is-pending'}`}>
                <Icon name={approved ? 'check-circle' : 'clock'} className="me-1" />
                {approved ? t('listingApproved') : t('listingPending')}
              </p>
            )}

            <label className="bravos-form__label">{t('headline')}</label>
            <input className="bravos-form__input" type="text" value={headline}
                   maxLength={200} placeholder={t('headlinePlaceholder')}
                   onChange={(e) => setHeadline(e.target.value)} />

            <label className="bravos-form__label">{t('servicesOffered')}</label>
            <textarea className="bravos-form__input" rows={5} value={services}
                      placeholder={t('servicesPlaceholder')}
                      onChange={(e) => setServices(e.target.value)} />
            <p className="bravos-q__hint">{t('servicesHelp')}</p>

            <label className="bravos-form__label">{t('memberDiscountNote')}</label>
            <input className="bravos-form__input" type="text" value={discountNote}
                   maxLength={300} placeholder={t('memberDiscountPlaceholder')}
                   onChange={(e) => setDiscountNote(e.target.value)} />

            <label className="bravos-form__label">{t('bravosCommission')}</label>
            <input className="bravos-form__input" type="text" value={commission}
                   maxLength={200} placeholder={t('bravosCommissionPlaceholder')}
                   onChange={(e) => setCommission(e.target.value)} />
            <p className="bravos-q__hint">{t('bravosCommissionHelp')}</p>

            <Title my="1" className="mt-4">{t('hostProfileHeading')}</Title>
            <p className="bravos-q__hint">{t('hostProfileHelp')}</p>

            <label className="bravos-form__label">{t('publicBio')}</label>
            <textarea className="bravos-form__input" rows={5} value={publicBio}
                      placeholder={t('publicBioPlaceholder')}
                      onChange={(e) => setPublicBio(e.target.value)} />

            <label className="bravos-form__label">LinkedIn</label>
            <input className="bravos-form__input" type="url" value={linkedin}
                   placeholder="https://linkedin.com/in/…"
                   onChange={(e) => setLinkedin(e.target.value)} />

            <label className="bravos-form__label">Instagram</label>
            <input className="bravos-form__input" type="url" value={instagram}
                   placeholder="https://instagram.com/…"
                   onChange={(e) => setInstagram(e.target.value)} />

            <label className="bravos-form__label">Facebook</label>
            <input className="bravos-form__input" type="url" value={facebook}
                   placeholder="https://facebook.com/…"
                   onChange={(e) => setFacebook(e.target.value)} />

            <label className="bravos-form__label">X / Twitter</label>
            <input className="bravos-form__input" type="url" value={twitter}
                   placeholder="https://x.com/…"
                   onChange={(e) => setTwitter(e.target.value)} />

            <label className="bravos-form__label">{t('website')}</label>
            <input className="bravos-form__input" type="url" value={website}
                   placeholder="https://…"
                   onChange={(e) => setWebsite(e.target.value)} />

            {notice && <p className="bravos-cal__notice mt-2">{notice}</p>}
            <button type="submit" className="bravos-event__claim mt-2" disabled={saving}>
              {saving ? '…' : t('save')}
            </button>
          </form>
        </Card>
      )}

      <BlogSubmission />
    </Base>
  );
};

export default EditSpecialistProfile;
